"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Section, Typography } from "@/components/ui";

export interface StoreHotspotItem {
  image: string;
  imageAlt: string;
  tag: string;
  tagColor: string;
  title: string;
  valuePoint: string;
  href: string;
  linkText: string;
  /** position of the pin on the store stage, as a percentage of stage width/height */
  left: number;
  top: number;
}

interface StoreHotspotSectionProps {
  id?: string;
  sectionLabel?: string;
  title: string;
  subtitle?: React.ReactNode;
  items: StoreHotspotItem[];
  className?: string;
  photoSrc?: string;
  photoAlt?: string;
  dismissOnHotspotLeave?: boolean;
  /** CSS aspect-ratio for the stage photo. Wider ratios reduce height. Default: 1536 / 1024 */
  aspectRatio?: string;
}

const CARD_W = 360;
const CARD_H_ESTIMATE = 140;
const GAP = 18;
const STAGE_MARGIN = 8;
const AUTO_CLOSE_DELAY = 3000;
const HOVER_CLOSE_DELAY = 120;

export const StoreHotspotSection: React.FC<StoreHotspotSectionProps> = ({
  id,
  sectionLabel,
  title,
  subtitle,
  items,
  className = "",
  photoSrc,
  photoAlt = "Retail store interior with digital signage screens",
  dismissOnHotspotLeave = false,
  aspectRatio = "1536 / 1024",
}) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [cardPos, setCardPos] = useState({ left: 0, top: 0 });

  const computePosition = (index: number, cardH: number, cardW: number) => {
    const stage = stageRef.current;
    const item = items[index];
    if (!stage || !item) return null;

    const rect = stage.getBoundingClientRect();
    const anchorX = (item.left / 100) * rect.width;
    const anchorY = (item.top / 100) * rect.height;

    let left = anchorX + GAP;
    if (left + cardW > rect.width - STAGE_MARGIN) {
      left = anchorX - cardW - GAP;
    }
    if (left < STAGE_MARGIN) left = STAGE_MARGIN;
    if (left + cardW > rect.width - STAGE_MARGIN)
      left = Math.max(STAGE_MARGIN, rect.width - cardW - STAGE_MARGIN);

    let top = anchorY - cardH / 2;
    if (top < STAGE_MARGIN) top = STAGE_MARGIN;
    if (top + cardH > rect.height - STAGE_MARGIN) top = rect.height - cardH - STAGE_MARGIN;

    return { left, top };
  };

  const estimateCardWidth = () => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return CARD_W;
    return Math.min(CARD_W, rect.width - STAGE_MARGIN * 2);
  };

  const cancelAutoClose = () => {
    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }
  };

  const openCard = (index: number) => {
    const pos = computePosition(index, CARD_H_ESTIMATE, estimateCardWidth());
    if (!pos) return;
    cancelAutoClose();
    setCardPos(pos);
    setActiveIndex(index);
  };

  const closeCard = () => {
    cancelAutoClose();
    setActiveIndex(null);
  };

  const scheduleClose = (delay: number) => {
    cancelAutoClose();
    autoCloseRef.current = setTimeout(() => setActiveIndex(null), delay);
  };

  // Re-measure against the card's real (variable) size once it has rendered,
  // so positioning lines up with the pin precisely and never overflows the stage.
  useLayoutEffect(() => {
    if (activeIndex === null || !cardRef.current) return;
    const realH = cardRef.current.offsetHeight;
    const realW = cardRef.current.offsetWidth;
    const pos = computePosition(activeIndex, realH, realW);
    if (!pos) return;
    setCardPos(prev => (prev.left === pos.left && prev.top === pos.top ? prev : pos));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // Dismiss on the way out of the whole stage, not per-dot, so the pointer can
  // travel from a dot to its card without the card vanishing underneath it.
  // The delay lets you come back and keep reading without the card flicking away.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const handleLeave = () => {
      scheduleClose(dismissOnHotspotLeave ? HOVER_CLOSE_DELAY : AUTO_CLOSE_DELAY);
    };
    stage.addEventListener("mouseleave", handleLeave);
    stage.addEventListener("mouseenter", cancelAutoClose);
    return () => {
      stage.removeEventListener("mouseleave", handleLeave);
      stage.removeEventListener("mouseenter", cancelAutoClose);
      cancelAutoClose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissOnHotspotLeave]);

  // Escape is the expected way out of an overlay, and it is the only way out for
  // keyboard users, who have no pointer to move off the stage.
  useEffect(() => {
    if (activeIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCard();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const activeItem = activeIndex !== null ? items[activeIndex] : null;

  return (
    <Section id={id} variant="default" className={className}>
      <div className="mb-10 lg:mb-12 text-center max-w-3xl mx-auto">
        {sectionLabel ? <span className="section-label">{sectionLabel}</span> : null}
        <Typography variant="h2" as="h2" className="mb-4">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body" as="p" className="text-text-secondary">
            {subtitle}
          </Typography>
        ) : null}
      </div>

      <div
        ref={stageRef}
        className="relative w-full rounded-2xl overflow-hidden shadow-[0_20px_46px_rgba(20,39,86,0.16)] mt-6"
        style={{
          aspectRatio,
          background: photoSrc
            ? undefined
            : "linear-gradient(155deg,#2a2f3a,#151821) center/cover no-repeat",
        }}
      >
        {photoSrc ? (
          <Image
            src={photoSrc}
            alt={photoAlt}
            fill
            priority={false}
            className="object-cover object-center"
            sizes="(max-width: 1240px) 100vw, 1240px"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-center px-8 text-white/55 text-sm pointer-events-none">
            [ Photo placeholder — swap in your real retail store photo here ]
          </div>
        )}

        <button
          type="button"
          onClick={closeCard}
          aria-label="Close use case card"
          className={`absolute inset-0 z-[4] transition-colors ${
            activeIndex !== null
              ? "bg-[rgba(11,18,48,0.35)] cursor-default"
              : "bg-transparent pointer-events-none"
          }`}
          tabIndex={activeIndex !== null ? 0 : -1}
        />

        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={item.title}
              type="button"
              onMouseEnter={() => openCard(index)}
              onMouseLeave={() => {
                if (dismissOnHotspotLeave) scheduleClose(HOVER_CLOSE_DELAY);
              }}
              onFocus={() => openCard(index)}
              // Kept for touch devices, where there is no hover to trigger on
              onClick={e => {
                e.stopPropagation();
                openCard(index);
              }}
              aria-label={item.title}
              aria-expanded={isActive}
              className={`absolute z-[5] w-4 h-4 rounded-full border-[3px] border-white shadow-[0_2px_8px_rgba(0,0,0,0.35)] -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-transform duration-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(11,18,48,0.6)] before:absolute before:-inset-3 before:rounded-full before:content-[''] ${
                isActive ? "scale-110" : "hs-pulse"
              }`}
              style={
                {
                  left: `${item.left}%`,
                  top: `${item.top}%`,
                  background: item.tagColor,
                  // Halo takes the pin's own colour so it stays visible on light parts of the photo
                  "--hs-pulse": item.tagColor,
                  boxShadow: isActive
                    ? "0 0 0 4px rgba(255,255,255,0.9), 0 2px 10px rgba(0,0,0,0.4)"
                    : undefined,
                } as React.CSSProperties
              }
            />
          );
        })}

        {activeItem ? (
          <div
            ref={cardRef}
            key={activeItem.title}
            className="absolute z-10 overflow-hidden hs-glass hs-card-in"
            style={
              {
                left: cardPos.left,
                top: cardPos.top,
                width: `min(${CARD_W}px, calc(100% - ${STAGE_MARGIN * 2}px))`,
                // Ties the card back to the pin it came from
                "--hs-accent": activeItem.tagColor,
              } as React.CSSProperties
            }
          >
            <span aria-hidden="true" className="hs-card-accent pointer-events-none" />
            {/* The whole card is the link — there is no separate CTA to aim at */}
            <Link
              href={activeItem.href}
              aria-label={activeItem.linkText}
              className="relative block p-5"
              onMouseEnter={cancelAutoClose}
              onMouseLeave={() => {
                if (dismissOnHotspotLeave) closeCard();
              }}
            >
              <span className="hs-tag inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide mb-3 text-white">
                {activeItem.tag}
              </span>
              <Typography
                as="h4"
                className="text-[17px] font-bold text-text-primary leading-tight mb-2"
              >
                {activeItem.title}
              </Typography>
              <Typography
                as="p"
                variant="small"
                className="text-[13px] text-text-secondary leading-relaxed"
              >
                So you can {activeItem.valuePoint}
              </Typography>
            </Link>
          </div>
        ) : null}
      </div>

      <Typography
        as="p"
        variant="small"
        className={`text-center mt-3.5 italic text-text-secondary ${activeIndex !== null ? "invisible" : ""}`}
      >
        <span className="hs-hint-hover">Hover over</span>
        <span className="hs-hint-tap">Tap</span> a dot on the photo above to see its use case.
      </Typography>

      <style jsx>{`
        .hs-pulse::after {
          content: "";
          position: absolute;
          inset: -3px;
          border-radius: 9999px;
          background: var(--hs-pulse, #ffffff);
          z-index: -1;
          animation: hsPulse 2.2s ease-out infinite;
        }
        @keyframes hsPulse {
          0% {
            transform: scale(1.15);
            opacity: 0.4;
          }
          70%,
          100% {
            transform: scale(2.1);
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hs-pulse::after {
            animation: none;
            opacity: 0.35;
            transform: scale(1.8);
          }
        }
        /* Near-opaque on purpose: the card carries body copy, and text over a
           translucent panel sitting on an unpredictable photo is not readable.
           The blur is what remains of the glass, not the transparency. */
        .hs-glass {
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.97);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
          backdrop-filter: blur(20px) saturate(140%);
          box-shadow:
            0 0 0 1px rgba(255, 255, 255, 0.8),
            0 18px 40px rgba(11, 18, 48, 0.35);
        }
        /* Colour bar keyed to the active pin, so the eye lands on the card */
        .hs-card-accent {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(
            90deg,
            var(--hs-accent, transparent),
            color-mix(in srgb, var(--hs-accent, #ffffff) 45%, #ffffff)
          );
          z-index: 1;
        }
        .hs-tag {
          background: linear-gradient(
            135deg,
            var(--hs-accent, #0b1230),
            color-mix(in srgb, var(--hs-accent, #0b1230) 70%, #000000)
          );
          box-shadow: 0 2px 8px color-mix(in srgb, var(--hs-accent, #0b1230) 50%, transparent);
        }
        /* The hint has to match the input the visitor actually has */
        .hs-hint-tap {
          display: none;
        }
        @media (hover: none) {
          .hs-hint-hover {
            display: none;
          }
          .hs-hint-tap {
            display: inline;
          }
        }
        .hs-card-in {
          animation: hsCardIn 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes hsCardIn {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hs-card-in {
            animation: none;
          }
        }
      `}</style>
    </Section>
  );
};
